import type { ReactNode } from "react";
import { Helmet } from "react-helmet-async";

interface MetaTagsProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: "website" | "article";
  canonicalUrl?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  children?: ReactNode;
}

export const MetaTags: React.FC<MetaTagsProps> = ({
  title,
  description,
  keywords,
  ogImage,
  ogType = "website",
  canonicalUrl,
  noIndex = false,
  noFollow = false,
  author,
  publishedTime,
  modifiedTime,
  children,
}) => {
  const fullTitle = title.includes("Microbeta")
    ? title
    : `${title} | Microbeta`;
  const baseUrl = import.meta.env.VITE_APP_BASE_URL || window.location.origin;

  // Use provided canonical URL or current URL
  const canonical = canonicalUrl || window.location.href;

  // Default OG image fallback
  const defaultOgImage = `${baseUrl}/android-chrome-512x512.png`;
  const ogImageUrl = ogImage || defaultOgImage;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />

      {/* Robots */}
      <meta
        name="robots"
        content={`${noIndex ? "noindex" : "index"},${noFollow ? "nofollow" : "follow"}`}
      />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:site_name" content="Microbeta" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {/* Article specific meta tags */}
      {ogType === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {ogType === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {ogType === "article" && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Additional children meta tags */}
      {children}
    </Helmet>
  );
};

// Utility function to create SEO-friendly descriptions
export const createSeoDescription = (
  content: string,
  type: "area" | "model" | "route",
  location?: string
): string => {
  const maxLength = 160;
  let description = "";

  switch (type) {
    case "area":
      description = `Explore ${content} climbing area${location ? ` in ${location}` : ""}. Discover routes, models, and climbing information.`;
      break;
    case "model":
      description = `model problem: ${content}${location ? ` in ${location}` : ""}. Get route details, grades, and climbing information.`;
      break;
    case "route":
      description = `Climbing route: ${content}${location ? ` in ${location}` : ""}. Find grade, description, and climbing details.`;
      break;
  }

  // Truncate if too long
  if (description.length > maxLength) {
    description = `${description.substring(0, maxLength - 3)}...`;
  }

  return description;
};

// Utility function to create SEO-friendly keywords
export const createSeoKeywords = (
  type: "area" | "model" | "route",
  name: string,
  location?: string
): string => {
  const baseKeywords = [
    "climbing",
    "rock climbing",
    "modeling",
    "outdoor climbing",
  ];
  const typeKeywords = {
    area: ["climbing area", "outdoor climbing", "climbing destination"],
    model: ["model problem", "modeling", "rock climbing"],
    route: ["climbing route", "rock climbing", "climbing grade"],
  };

  const keywords = [
    ...baseKeywords,
    ...typeKeywords[type],
    name.toLowerCase(),
    ...(location ? [location.toLowerCase()] : []),
  ];

  return keywords.join(", ");
};
